import React, { useState } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';
import RadioField from 'components/application/RadioField';

import { useCurrentActor } from 'components/contexts/CurrentActor';

import LoadingIcon from '-!svg-react-loader?name=LoadingIcon!icons/loading.svg';

export default function ExportProjectsModal({
  isExportProjectsDisabled,
  isFilteredSelectionDisabled,
  isGenerating,
  onClose,
  onSubmit,
}) {
  const currentActor = useCurrentActor();

  const pluralProjectName =
    currentActor.division.attributes.pluralRAMSName || 'RAMS';

  const [projectSelection, setProjectSelection] = useState(
    isFilteredSelectionDisabled ? 'unfiltered' : 'filtered',
  );

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {isGenerating ? 'Close' : 'Cancel'}
          </TextButton>
          {!isGenerating && (
            <FilledButton
              color='mint'
              disabled={isExportProjectsDisabled}
              onClick={() => onSubmit({ projectSelection })}
            >
              Generate CSV
            </FilledButton>
          )}
        </div>
      }
      isOpen={true}
      mode='flexible'
      onClose={onClose}
    >
      <div className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-6'>
        <p className='tw-mb-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
          {isGenerating ?
            'Downloading your CSV'
          : `Export ${pluralProjectName} to CSV`}
        </p>
      </div>
      {isGenerating ?
        <div className='tw-flex tw-flex-col tw-items-center tw-gap-6 tw-p-6'>
          <div className='tw-apply-loading-spinner--blue-light tw-flex tw-items-center tw-justify-center'>
            <LoadingIcon height={41} width={41} />
          </div>
          <p className='tw-mb-0 tw-text-l tw-font-semibold tw-tracking-auto'>
            Generating export
          </p>
          <p className='tw-mb-0 tw-text-m tw-font-normal tw-tracking-auto'>
            This may take a few minutes. Once finished, your export will
            automatically download. You can close this modal while you wait.
          </p>
        </div>
      : <div className='tw-flex tw-flex-col tw-gap-4 tw-p-6'>
          <p className='tw-mb-0 tw-text-l tw-font-semibold tw-tracking-auto'>
            Data included in the export
          </p>
          <div className='tw-flex tw-flex-col tw-gap-3'>
            <RadioField
              checked={projectSelection === 'filtered'}
              className='tw-m-0'
              disabled={isFilteredSelectionDisabled}
              key='filtered'
              label={
                <p className='tw-mb-0 tw-text-m tw-font-normal tw-tracking-auto'>
                  Only {pluralProjectName} visible after filtering/searching
                </p>
              }
              name='projectSelection'
              onChange={() => setProjectSelection('filtered')}
              value='filtered'
            />
            <RadioField
              checked={projectSelection === 'unfiltered'}
              className='tw-m-0'
              key='unfiltered'
              label={
                <p className='tw-mb-0 tw-text-m tw-font-normal tw-tracking-auto'>
                  All {pluralProjectName} (including those not visible on
                  filtering)
                </p>
              }
              name='projectSelection'
              onChange={() => setProjectSelection('unfiltered')}
              value='unfiltered'
            />
          </div>
        </div>
      }
    </ModalBox>
  );
}

ExportProjectsModal.propTypes = {
  isExportProjectsDisabled: PropTypes.bool,
  isFilteredSelectionDisabled: PropTypes.bool.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
